import ImageCapture from "./ImageCapture";
import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { Form, Button, Container, Row, Col, ProgressBar, Card, Alert, Spinner, Accordion, Badge, InputGroup, Collapse } from "react-bootstrap";
import SignatureCanvas from "react-signature-canvas";
import { useSearchParams, useNavigate } from "react-router-dom";
import { authorizedRequest } from "../../api/authorizedRequest";
import Swal from "sweetalert2";
import { useLuckyOrange } from '../../hooks/useLuckyOrange';

const EmployeePacket: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const applicationId = searchParams.get('applicationId');
  const token = searchParams.get('token');
  const isCandidate = !!token; // If token exists, it's a candidate

  // ── Lucky Orange analytics ──────────────────────────────────────────────
  // Both methods are queue-safe: safe to call before the LO script loads.
  const { trackMilestone, identifyApplication } = useLuckyOrange();

  /** Human-readable names for each packet step, used in event metadata. */
  const PACKET_STEP_NAMES: Record<number, string> = {
    1: 'Personal Information & Emergency Contacts',
    2: 'Direct Deposit Setup',
    3: 'Documents & Signature',
  };
  
  const [isLoadingApplication, setIsLoadingApplication] = useState(false);
  const [applicationLoadError, setApplicationLoadError] = useState<string | null>(null);
  const [applicationData, setApplicationData] = useState<any>(null);

  type FormDataType = {
    lastName: string;
    firstName: string;
    middleInitial: string;
    otherLastNames: string;
    address: string;
    aptNumber: string;
    cityOrTown: string;
    state: string;
    zipCode: string;
    dateOfBirth: string;
    ssn: string;
    attestation: string;
    attestationExpDate: string;
    attestationUSCIS: string;
    attestationI94: string;
    attestationPassport: string;
    attestationCountry: string;
    documentType: string[];
    documentFiles: File[];
    documentImage: File | null;
    signature: string;
    signatureType: string; // "draw" | "type" | "upload"
    typedSignature: string;
    imageChoice?: "camera" | "upload";
    userPhoto: File | null;
    userPhotoPreview: string | null;
    disclaimerAcknowledged: boolean;
    // W-4 Emergency Contact Information
    emergencyContactName: string;
    emergencyContactRelationship: string;
    emergencyContactPhone: string;
    emergencyContactEmail: string;
    // Secondary Emergency Contact Information
    secondaryEmergencyContactName: string;
    secondaryEmergencyContactRelationship: string;
    secondaryEmergencyContactPhone: string;
    secondaryEmergencyContactEmail: string;
    // W-4 Direct Deposit Information
    bankName: string;
    routingNumber: string;
    accountNumber: string;
    accountType: string; // "checking" | "savings"
    depositAmount: string;
    directDepositDate: string;
    // Secondary bank account for remaining balance
    secondaryBankName: string;
    secondaryRoutingNumber: string;
    secondaryAccountNumber: string;
    secondaryAccountType: string;
  };

  const [formData, setFormData] = useState<FormDataType>({
    lastName: "",
    firstName: "",
    middleInitial: "",
    otherLastNames: "",
    address: "",
    aptNumber: "",
    cityOrTown: "",
    state: "",
    zipCode: "",
    dateOfBirth: "",
    ssn: "",
    attestation: "",
    attestationExpDate: "",
    attestationUSCIS: "",
    attestationI94: "",
    attestationPassport: "",
    attestationCountry: "",
    documentType: [],
    documentFiles: [],
    documentImage: null,
    signature: "",
    signatureType: "",
    typedSignature: "",
    userPhoto: null,
    userPhotoPreview: null,
    disclaimerAcknowledged: false,
    // W-4 Emergency Contact Information
    emergencyContactName: "",
    emergencyContactRelationship: "",
    emergencyContactPhone: "",
    emergencyContactEmail: "",
    // Secondary Emergency Contact Information
    secondaryEmergencyContactName: "",
    secondaryEmergencyContactRelationship: "",
    secondaryEmergencyContactPhone: "",
    secondaryEmergencyContactEmail: "",
    // W-4 Direct Deposit Information
    bankName: "",
    routingNumber: "",
    accountNumber: "",
    accountType: "", // Keep empty for optional withholding
    depositAmount: "",
    directDepositDate: new Date().toLocaleDateString(),
    // Secondary bank account for remaining balance
    secondaryBankName: "",
    secondaryRoutingNumber: "",
    secondaryAccountNumber: "",
    secondaryAccountType: "checking", // Net pay account can have default
  });

  const [currentStep, setCurrentStep] = useState(1);
  const sigCanvas = useRef<SignatureCanvas>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isValidating, setIsValidating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [optionalWithholdingOpen, setOptionalWithholdingOpen] = useState(false);
  const optionalWithholdingTouchedRef = useRef(false);

  const hasWithholdingData = useMemo(() => {
    const {
      bankName,
      accountType,
      routingNumber,
      accountNumber,
      depositAmount
    } = formData;

    return Boolean(
      bankName?.trim() ||
      accountType ||
      routingNumber?.trim() ||
      accountNumber?.trim() ||
      depositAmount?.trim()
    );
  }, [formData]);

  const hasWithholdingErrors = useMemo(() => (
    errors.bankName ||
    errors.accountType ||
    errors.routingNumber ||
    errors.accountNumber ||
    errors.depositAmount
  ), [errors]);

  const withholdingSummaryChips = useMemo(() => {
    if (!hasWithholdingData) {
      return [
        {
          text: "Skipped",
          variant: "secondary"
        }
      ];
    }

    const chips: Array<{ text: string; variant: string }> = [];
    if (formData.depositAmount) {
      chips.push({ text: `$${formData.depositAmount.trim()} per pay`, variant: "primary" });
    }
    if (formData.accountType) {
      chips.push({ text: `${formData.accountType === "savings" ? "Savings" : "Checking"}`, variant: "info" });
    }
    if (formData.bankName) {
      chips.push({ text: formData.bankName.trim(), variant: "light" });
    }
    return chips.length ? chips : [{ text: "Details provided", variant: "light" }];
  }, [formData, hasWithholdingData]);

  useEffect(() => {
    if (optionalWithholdingTouchedRef.current) return;
    if (hasWithholdingData || hasWithholdingErrors) {
      setOptionalWithholdingOpen(true);
    }
  }, [hasWithholdingData, hasWithholdingErrors]);

  // ── Milestone: Employee Packet Started ───────────────────────────────
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    trackMilestone('Employee Packet Started', {
      isCandidate,
      // applicationId is safe to include — it is a database reference, not PII
      ...(applicationId ? { applicationId } : {}),
    });
    // Identify the session if we already have the applicationId (staff flow)
    if (applicationId) {
      identifyApplication(applicationId);
    }
  }, []); // intentionally empty — fire once on form entry

  // Fetch application data when applicationId is provided
  useEffect(() => {
    const fetchApplicationData = async () => {
      if (!applicationId) return;

      setIsLoadingApplication(true);
      setApplicationLoadError(null);

      try {
        // Use different endpoint based on whether it's a candidate or logged-in user
        const endpoint = isCandidate 
          ? `/JobApplication/${applicationId}/CandidateApplication?candidatetoken=${token}`
          : `/JobApplication/${applicationId}`;

        const response = await authorizedRequest({
          url: endpoint,
          method: 'GET'
        });

        const appData = response.data;
        setApplicationData(appData);

        // Prefill form data
        setFormData(prevData => ({
          ...prevData,
          firstName: appData.firstName || "",
          lastName: appData.lastName || "",
          address: appData.streetAddress || "",
          aptNumber: appData.addressLine2 || "",
          cityOrTown: appData.city || "",
          state: appData.stateOrProvince || "",
          zipCode: appData.postalCode || "",
          dateOfBirth: appData.birthDate ? new Date(appData.birthDate).toISOString().split('T')[0] : "",
          // Note: SSN is not typically stored in job applications for security reasons
          // middleInitial and otherLastNames may need to be parsed from name fields if available
        }));

      } catch (error) {
        console.error('Error fetching application data:', error);
        setApplicationLoadError('Failed to load application data. Please try again.');
      } finally {
        setIsLoadingApplication(false);
      }
    };

    fetchApplicationData();
  }, [applicationId, isCandidate, token]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }
  };

  // Validation functions
  const validateStep1 = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Required field validations
    if (!formData.lastName.trim()) newErrors.lastName = "Last name is required";
    if (!formData.firstName.trim()) newErrors.firstName = "First name is required";
    if (!formData.address.trim()) newErrors.address = "Street address is required";
    if (!formData.cityOrTown.trim()) newErrors.cityOrTown = "City is required";
    if (!formData.state) newErrors.state = "State is required";
    if (!formData.zipCode.trim()) newErrors.zipCode = "ZIP code is required";
    if (!formData.dateOfBirth) newErrors.dateOfBirth = "Date of birth is required";
    if (!formData.ssn.trim()) newErrors.ssn = "Social Security Number is required";
    if (!formData.attestation) newErrors.attestation = "Employment authorization status is required";

    // Format validations
    const zipRegex = /^\d{5}(-\d{4})?$/;
    if (formData.zipCode && !zipRegex.test(formData.zipCode)) {
      newErrors.zipCode = "Invalid ZIP code format (e.g., 12345 or 12345-6789)";
    }

    const ssnRegex = /^\d{3}-?\d{2}-?\d{4}$/;
    if (formData.ssn && !ssnRegex.test(formData.ssn.replace(/\D/g, '').replace(/(\d{3})(\d{2})(\d{4})/, '$1-$2-$3'))) {
      newErrors.ssn = "Invalid SSN format (e.g., 123-45-6789)";
    }

    // Date validation (cannot be in the future)
    if (formData.dateOfBirth) {
      const birthDate = new Date(formData.dateOfBirth);
      const today = new Date();
      
      if (birthDate > today) {
        newErrors.dateOfBirth = "Birth date cannot be in the future";
      }
    }

    // Primary Emergency Contact validation
    if (!formData.emergencyContactName.trim()) {
      newErrors.emergencyContactName = "Primary emergency contact name is required";
    }
    if (!formData.emergencyContactRelationship.trim()) {
      newErrors.emergencyContactRelationship = "Primary emergency contact relationship is required";
    }
    if (!formData.emergencyContactPhone.trim()) {
      newErrors.emergencyContactPhone = "Primary emergency contact phone is required";
    } else if (!/^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/.test(formData.emergencyContactPhone)) {
      newErrors.emergencyContactPhone = "Please enter a valid phone number";
    }
    if (!formData.emergencyContactEmail.trim()) {
      newErrors.emergencyContactEmail = "Primary emergency contact email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.emergencyContactEmail)) {
      newErrors.emergencyContactEmail = "Please enter a valid email address";
    }

    // Secondary Emergency Contact validation (optional, but if any field is provided, validate all required fields)
    const hasSecondaryContact = formData.secondaryEmergencyContactName.trim() || 
                               formData.secondaryEmergencyContactRelationship.trim() || 
                               formData.secondaryEmergencyContactPhone.trim() || 
                               formData.secondaryEmergencyContactEmail.trim();

    if (hasSecondaryContact) {
      if (!formData.secondaryEmergencyContactName.trim()) {
        newErrors.secondaryEmergencyContactName = "Secondary emergency contact name is required when any secondary contact info is provided";
      }
      if (!formData.secondaryEmergencyContactRelationship.trim()) {
        newErrors.secondaryEmergencyContactRelationship = "Secondary emergency contact relationship is required";
      }
      if (!formData.secondaryEmergencyContactPhone.trim()) {
        newErrors.secondaryEmergencyContactPhone = "Secondary emergency contact phone is required";
      } else if (!/^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/.test(formData.secondaryEmergencyContactPhone)) {
        newErrors.secondaryEmergencyContactPhone = "Please enter a valid phone number";
      }
      if (!formData.secondaryEmergencyContactEmail.trim()) {
        newErrors.secondaryEmergencyContactEmail = "Secondary emergency contact email is required";
      } else if (!/\S+@\S+\.\S+/.test(formData.secondaryEmergencyContactEmail)) {
        newErrors.secondaryEmergencyContactEmail = "Please enter a valid email address";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Optional Withholding validation - only validate if any field is filled
    const bankNameFilled = formData.bankName?.trim() || '';
    const accountTypeFilled = formData.accountType || '';
    const routingNumberFilled = formData.routingNumber?.trim() || '';
    const accountNumberFilled = formData.accountNumber?.trim() || '';
    const depositAmountFilled = formData.depositAmount?.trim() || '';

    const hasWithholdingInfo = bankNameFilled || 
                              accountTypeFilled || 
                              routingNumberFilled || 
                              accountNumberFilled || 
                              depositAmountFilled;

    // IMPORTANT: Only validate withholding fields if user has actually entered any withholding information
    if (hasWithholdingInfo) {
      // If any withholding field is filled, validate all required withholding fields
      if (!bankNameFilled) {
        newErrors.bankName = "Withholding bank name is required when withholding is specified";
      }
      if (!accountTypeFilled) {
        newErrors.accountType = "Withholding account type is required when withholding is specified";
      }
      if (!routingNumberFilled) {
        newErrors.routingNumber = "Withholding routing number is required when withholding is specified";
      } else if (!/^\d{9}$/.test(routingNumberFilled)) {
        newErrors.routingNumber = "Withholding routing number must be exactly 9 digits";
      }
      if (!accountNumberFilled) {
        newErrors.accountNumber = "Withholding account number is required when withholding is specified";
      }
      if (!depositAmountFilled) {
        newErrors.depositAmount = "Withholding amount is required when withholding account is specified";
      } else {
        // Validate deposit amount format if provided
        const amount = parseFloat(depositAmountFilled);
        if (isNaN(amount) || amount <= 0) {
          newErrors.depositAmount = "Withholding amount must be a valid positive number";
        }
      }
    }
    // If no withholding info is provided, skip all withholding validation completely

    // Net Pay validation (always required)
    if (!formData.secondaryBankName.trim()) {
      newErrors.secondaryBankName = "Net pay bank name is required";
    }
    if (!formData.secondaryAccountType) {
      newErrors.secondaryAccountType = "Net pay account type is required";
    }
    if (!formData.secondaryRoutingNumber.trim()) {
      newErrors.secondaryRoutingNumber = "Net pay routing number is required";
    } else if (!/^\d{9}$/.test(formData.secondaryRoutingNumber)) {
      newErrors.secondaryRoutingNumber = "Net pay routing number must be exactly 9 digits";
    }
    if (!formData.secondaryAccountNumber.trim()) {
      newErrors.secondaryAccountNumber = "Net pay account number is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep3 = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Photo validation
    if (!formData.userPhoto && !formData.userPhotoPreview) {
      newErrors.userPhoto = "Please upload or capture a photo";
    }

    // Document type validation
    if (formData.documentType.length === 0) {
      newErrors.documentType = "Please select at least one document type";
    }

    // Document upload validation
    if (formData.documentFiles.length === 0) {
      newErrors.documentFiles = "Please upload at least one supporting document";
    }

    // Signature validation
    if (!formData.signature && formData.signatureType !== "type") {
      newErrors.signature = "Digital signature is required";
    } else if (formData.signatureType === "type" && !formData.typedSignature.trim()) {
      newErrors.signature = "Please enter your name for typed signature";
    }

    // Disclaimer acknowledgement validation
    if (!formData.disclaimerAcknowledged) {
      newErrors.disclaimerAcknowledged = "You must acknowledge the disclaimer before submitting";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    setIsValidating(true);
    
    let isValid = false;
    if (currentStep === 1) {
      isValid = validateStep1();
    } else if (currentStep === 2) {
      isValid = validateStep2(); // Direct Deposit validation
    } else if (currentStep === 3) {
      isValid = validateStep3(); // Documents & Signature validation
    }
    
    if (isValid) {
      if (currentStep < 3) {
        // MILESTONE: Employee Packet Step Completed
        trackMilestone('Employee Packet Step Completed', {
          step: currentStep,
          stepName: PACKET_STEP_NAMES[currentStep],
        });
        setCurrentStep(currentStep + 1);
      } else {
        // Last step - submit the form
        const syntheticEvent = new Event('submit') as any;
        syntheticEvent.preventDefault = () => {};
        handleSubmit(syntheticEvent);
      }
    } else {
      // MILESTONE: Validation Error — signals where candidates struggle
      trackMilestone('Validation Error', {
        step: currentStep,
        stepName: PACKET_STEP_NAMES[currentStep],
        formName: 'EmployeePacket',
      });
      // Scroll to first error
      setTimeout(() => {
        const firstErrorElement = document.querySelector('.is-invalid');
        if (firstErrorElement) {
          firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
    
    setIsValidating(false);
  };
  const handlePrevious = () => setCurrentStep((s) => s - 1);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const newFiles = Array.from(files);
      setFormData((prev) => ({ 
        ...prev, 
        documentFiles: [...prev.documentFiles, ...newFiles] 
      }));
    }
  };

  // Handle document type selection (toggle)
  const handleDocumentTypeToggle = (docType: string) => {
    setFormData((prev) => ({
      ...prev,
      documentType: prev.documentType.includes(docType)
        ? prev.documentType.filter(type => type !== docType)
        : [...prev.documentType, docType]
    }));
  };

  // Handle removing a specific document
  const handleRemoveDocument = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      documentFiles: prev.documentFiles.filter((_, i) => i !== index)
    }));
  };

  // Helper function to generate typed signature canvas
  const generateTypedSignature = (text: string): string => {
    const canvas = document.createElement('canvas');
    canvas.width = 420;
    canvas.height = 150;
    const ctx = canvas.getContext('2d');
    
    if (!ctx) return '';
    
    // Set canvas background
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Set text style for a signature look
    ctx.fillStyle = 'black';
    ctx.font = '32px cursive, serif'; // Use cursive font for signature appearance
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    
    // Draw the text
    ctx.fillText(text, canvas.width / 2, canvas.height / 2);
    
    return canvas.toDataURL('image/png');
  };

  // Handle signature upload for uploaded signature files
  const handleSignatureUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files[0]) {
      const file = files[0];
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData((prev) => ({ 
          ...prev, 
          signature: reader.result as string 
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle signature operations
  const clearSignature = () => {
    if (formData.signatureType === "draw" && sigCanvas.current) {
      sigCanvas.current.clear();
    }
    setFormData((prev) => ({ ...prev, signature: "", typedSignature: "" }));
    setErrors(prev => ({ ...prev, signature: "" }));
  };

  const saveSignature = () => {
    // Handle typed signature
    if (formData.signatureType === "type" && formData.typedSignature.trim()) {
      const typedSigImage = generateTypedSignature(formData.typedSignature);
      setFormData((prev) => ({ ...prev, signature: typedSigImage }));
      if (errors.signature) {
        setErrors(prev => ({ ...prev, signature: "" }));
      }
      return;
    }

    // Handle drawn signature
    if (sigCanvas.current && formData.signatureType === "draw") {
      try {
        // Check if canvas is empty
        const canvas = sigCanvas.current.getCanvas();
        const context = canvas.getContext('2d');
        
        if (!context) {
          setErrors(prev => ({ ...prev, signature: "Unable to access signature canvas" }));
          return;
        }
        
        const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        
        // Check if canvas has any non-white pixels
        let isEmpty = true;
        for (let i = 0; i < data.length; i += 4) {
          if (data[i] !== 255 || data[i + 1] !== 255 || data[i + 2] !== 255 || data[i + 3] !== 0) {
            isEmpty = false;
            break;
          }
        }
        
        if (isEmpty) {
          setErrors(prev => ({ ...prev, signature: "Please provide a signature before saving" }));
          return;
        }
        
        // Use toDataURL instead of getTrimmedCanvas
        const dataUrl = canvas.toDataURL("image/png");
        setFormData((prev) => ({ ...prev, signature: dataUrl }));
        
        // Clear any existing signature error
        if (errors.signature) {
          setErrors(prev => ({ ...prev, signature: "" }));
        }
      } catch (error) {
        console.error("Error saving signature:", error);
        setErrors(prev => ({ ...prev, signature: "Error saving signature. Please try again." }));
      }
    }
  };

    // Handle user photo upload
    const handleUserPhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files[0]) {
        const file = files[0];
        const reader = new FileReader();
        reader.onloadend = () => {
          setFormData((prev) => ({ ...prev, userPhoto: file, userPhotoPreview: reader.result as string }));
        };
        reader.readAsDataURL(file);
      }
    };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsValidating(true);
    
    const isValid = validateStep3();
    
    if (isValid && applicationId) {
      setIsSubmitting(true);
      try {
        // Create FormData for multipart/form-data submission
        const formDataToSubmit = new FormData();
        
        // Add basic form fields
        formDataToSubmit.append('ApplicationId', applicationId);
        formDataToSubmit.append('LastName', formData.lastName);
        formDataToSubmit.append('FirstName', formData.firstName);
        formDataToSubmit.append('MiddleInitial', formData.middleInitial);
        formDataToSubmit.append('OtherLastNames', formData.otherLastNames);
        formDataToSubmit.append('Address', formData.address);
        formDataToSubmit.append('AptNumber', formData.aptNumber);
        formDataToSubmit.append('CityOrTown', formData.cityOrTown);
        formDataToSubmit.append('State', formData.state);
        formDataToSubmit.append('ZipCode', formData.zipCode);
        formDataToSubmit.append('DateOfBirth', formData.dateOfBirth);
        formDataToSubmit.append('SSN', formData.ssn);
        formDataToSubmit.append('Attestation', formData.attestation);
        
        // Add optional attestation fields
        if (formData.attestationExpDate) formDataToSubmit.append('AttestationExpDate', formData.attestationExpDate);
        if (formData.attestationUSCIS) formDataToSubmit.append('AttestationUSCIS', formData.attestationUSCIS);
        if (formData.attestationI94) formDataToSubmit.append('AttestationI94', formData.attestationI94);
        if (formData.attestationPassport) formDataToSubmit.append('AttestationPassport', formData.attestationPassport);
        if (formData.attestationCountry) formDataToSubmit.append('AttestationCountry', formData.attestationCountry);
        
        // Add document types as comma-separated string
        if (formData.documentType.length > 0) {
          formDataToSubmit.append('DocumentTypes', formData.documentType.join(','));
        }
        
        // Add signature as base64
        if (formData.signature) {
          formDataToSubmit.append('SignatureBase64', formData.signature);
        }
        
        // Add disclaimer acknowledgement
        formDataToSubmit.append('DisclaimerAcknowledged', formData.disclaimerAcknowledged.toString());
        
        // Add W-4 Emergency Contact Information
        formDataToSubmit.append('EmergencyContactName', formData.emergencyContactName);
        formDataToSubmit.append('EmergencyContactRelationship', formData.emergencyContactRelationship);
        formDataToSubmit.append('EmergencyContactPhone', formData.emergencyContactPhone);
        formDataToSubmit.append('EmergencyContactEmail', formData.emergencyContactEmail);
        
        // Add W-4 Secondary Emergency Contact Information (optional)
        formDataToSubmit.append('SecondaryEmergencyContactName', formData.secondaryEmergencyContactName);
        formDataToSubmit.append('SecondaryEmergencyContactRelationship', formData.secondaryEmergencyContactRelationship);
        formDataToSubmit.append('SecondaryEmergencyContactPhone', formData.secondaryEmergencyContactPhone);
        formDataToSubmit.append('SecondaryEmergencyContactEmail', formData.secondaryEmergencyContactEmail);
        
        // Add W-4 Direct Deposit Information
        formDataToSubmit.append('BankName', formData.bankName);
        formDataToSubmit.append('RoutingNumber', formData.routingNumber);
        formDataToSubmit.append('AccountNumber', formData.accountNumber);
        formDataToSubmit.append('AccountType', formData.accountType);
        formDataToSubmit.append('DepositAmount', formData.depositAmount);
        formDataToSubmit.append('DirectDepositDate', formData.directDepositDate);
        
        // Add Secondary Bank Account Information (for remaining balance if specific amount is entered)
        formDataToSubmit.append('SecondaryBankName', formData.secondaryBankName);
        formDataToSubmit.append('SecondaryRoutingNumber', formData.secondaryRoutingNumber);
        formDataToSubmit.append('SecondaryAccountNumber', formData.secondaryAccountNumber);
        formDataToSubmit.append('SecondaryAccountType', formData.secondaryAccountType);
        
        // Add user photo
        if (formData.userPhoto) {
          formDataToSubmit.append('userPhoto', formData.userPhoto);
        }
        
        // Add document files
        formData.documentFiles.forEach((file, index) => {
          formDataToSubmit.append('documentFiles', file);
        });

        // Submit to API
        const endpoint = isCandidate 
          ? `/JobApplication/candidate-i9-submission?token=${token}`
          : '/JobApplication/i9-submission';

        const response = await authorizedRequest({
          url: endpoint,
          method: 'POST',
          data: formDataToSubmit,
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        if (response.data.success) {
          setSubmitSuccess(true);

          // MILESTONE: Employee Packet Submitted
          // IDENTIFY: correlate session with applicationId (non-PII database reference)
          if (applicationId) identifyApplication(applicationId);
          trackMilestone('Employee Packet Submitted', {
            isCandidate,
            ...(applicationId ? { applicationId } : {}),
          });
          
          // Show success message and redirect based on user type
          Swal.fire({
            title: 'Success!',
            text: isCandidate 
              ? 'Your employee packet has been submitted successfully!' 
              : 'Employee Packet Submitted Successfully! PDF and ZIP files have been generated.',
            icon: 'success',
            confirmButtonText: 'OK',
            confirmButtonColor: '#28a745'
          }).then((result) => {
            if (result.isConfirmed) {
              // If user is a candidate (has token), redirect to employee packet thank you page
              // If user is staff (no token), redirect to applications list
              if (isCandidate) {
                navigate('/employee-packet-thank-you');
              } else {
                navigate('/applications');
              }
            }
          });
          console.log("Submission result:", response.data);
        } else {
          Swal.fire({
            title: 'Submission Failed',
            text: response.data.errorMessage || 'Form submission failed. Please try again.',
            icon: 'error',
            confirmButtonText: 'OK',
            confirmButtonColor: '#dc3545'
          });
        }
        
      } catch (error) {
        console.error('Error submitting Employee Packet:', error);
        Swal.fire({
          title: 'Error',
          text: 'An error occurred while submitting the form. Please try again.',
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#dc3545'
        });
      } finally {
        setIsSubmitting(false);
      }
    } else if (!applicationId) {
      Swal.fire({
        title: 'Missing Information',
        text: 'Application ID is required but not found.',
        icon: 'warning',
        confirmButtonText: 'OK',
        confirmButtonColor: '#ffc107'
      });
    } else {
      // Scroll to first error
      setTimeout(() => {
        const firstErrorElement = document.querySelector('.is-invalid');
        if (firstErrorElement) {
          firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
    
    setIsValidating(false);
  };

  const getProgress = () => {
    if (currentStep === 1) return 33;
    if (currentStep === 2) return 66;
    return 100;
  };

  // US State abbreviations
  const usStates = [
    { value: "", label: "-- Select State --" },
    { value: "AL", label: "AL - Alabama" },
    { value: "AK", label: "AK - Alaska" },
    { value: "AZ", label: "AZ - Arizona" },
    { value: "AR", label: "AR - Arkansas" },
    { value: "CA", label: "CA - California" },
    { value: "CO", label: "CO - Colorado" },
    { value: "CT", label: "CT - Connecticut" },
    { value: "DE", label: "DE - Delaware" },
    { value: "FL", label: "FL - Florida" },
    { value: "GA", label: "GA - Georgia" },
    { value: "HI", label: "HI - Hawaii" },
    { value: "ID", label: "ID - Idaho" },
    { value: "IL", label: "IL - Illinois" },
    { value: "IN", label: "IN - Indiana" },
    { value: "IA", label: "IA - Iowa" },
    { value: "KS", label: "KS - Kansas" },
    { value: "KY", label: "KY - Kentucky" },
    { value: "LA", label: "LA - Louisiana" },
    { value: "ME", label: "ME - Maine" },
    { value: "MD", label: "MD - Maryland" },
    { value: "MA", label: "MA - Massachusetts" },
    { value: "MI", label: "MI - Michigan" },
    { value: "MN", label: "MN - Minnesota" },
    { value: "MS", label: "MS - Mississippi" },
    { value: "MO", label: "MO - Missouri" },
    { value: "MT", label: "MT - Montana" },
    { value: "NE", label: "NE - Nebraska" },
    { value: "NV", label: "NV - Nevada" },
    { value: "NH", label: "NH - New Hampshire" },
    { value: "NJ", label: "NJ - New Jersey" },
    { value: "NM", label: "NM - New Mexico" },
    { value: "NY", label: "NY - New York" },
    { value: "NC", label: "NC - North Carolina" },
    { value: "ND", label: "ND - North Dakota" },
    { value: "OH", label: "OH - Ohio" },
    { value: "OK", label: "OK - Oklahoma" },
    { value: "OR", label: "OR - Oregon" },
    { value: "PA", label: "PA - Pennsylvania" },
    { value: "RI", label: "RI - Rhode Island" },
    { value: "SC", label: "SC - South Carolina" },
    { value: "SD", label: "SD - South Dakota" },
    { value: "TN", label: "TN - Tennessee" },
    { value: "TX", label: "TX - Texas" },
    { value: "UT", label: "UT - Utah" },
    { value: "VT", label: "VT - Vermont" },
    { value: "VA", label: "VA - Virginia" },
    { value: "WA", label: "WA - Washington" },
    { value: "WV", label: "WV - West Virginia" },
    { value: "WI", label: "WI - Wisconsin" },
    { value: "WY", label: "WY - Wyoming" },
    { value: "DC", label: "DC - District of Columbia" },
  ];

  // --- STEP RENDERERS ---
  const renderStep1 = () => (
    <div>
      {/* Employee Packet Section */}
      <Card className="mb-4 shadow-sm">
        <Card.Header className="bg-primary text-white">
          <h5 className="mb-0">👤 Employee Packet - Personal Information</h5>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Last Name *</Form.Label>
                <Form.Control
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                  isInvalid={!!errors.lastName}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.lastName}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>First Name *</Form.Label>
                <Form.Control
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                  isInvalid={!!errors.firstName}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.firstName}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Middle Initial</Form.Label>
                <Form.Control
                  type="text"
                  name="middleInitial"
                  value={formData.middleInitial}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Other Last Names Used</Form.Label>
                <Form.Control
                  type="text"
                  name="otherLastNames"
                  value={formData.otherLastNames}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Street Address *</Form.Label>
            <Form.Control
              type="text"
              name="address"
              value={formData.address}
              onChange={handleChange}
              required
              isInvalid={!!errors.address}
            />
            <Form.Control.Feedback type="invalid">
              {errors.address}
            </Form.Control.Feedback>
          </Form.Group>

          <Row>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Apt #</Form.Label>
                <Form.Control
                  type="text"
                  name="aptNumber"
                  value={formData.aptNumber}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>City *</Form.Label>
                <Form.Control
                  type="text"
                  name="cityOrTown"
                  value={formData.cityOrTown}
                  onChange={handleChange}
                  required
                  isInvalid={!!errors.cityOrTown}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.cityOrTown}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>State *</Form.Label>
                <Form.Select
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  required
                  isInvalid={!!errors.state}
                >
                  {usStates.map((state) => (
                    <option key={state.value} value={state.value}>
                      {state.label}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.state}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>ZIP Code *</Form.Label>
                <Form.Control
                  type="text"
                  name="zipCode"
                  value={formData.zipCode}
                  onChange={handleChange}
                  required
                  isInvalid={!!errors.zipCode}
                  placeholder="12345 or 12345-6789"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.zipCode}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Date of Birth *</Form.Label>
                <Form.Control
                  type="date"
                  name="dateOfBirth"
                  value={formData.dateOfBirth}
                  onChange={handleChange}
                  required
                  isInvalid={!!errors.dateOfBirth}
                  max={new Date().toISOString().split('T')[0]}
                  // data-lo-mask: prevents Lucky Orange recording this value
                  data-lo-mask
                />
                <Form.Control.Feedback type="invalid">
                  {errors.dateOfBirth}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Social Security Number *</Form.Label>
                <Form.Control
                  type="text"
                  name="ssn"
                  value={formData.ssn}
                  onChange={handleChange}
                  placeholder="XXX-XX-XXXX"
                  required
                  isInvalid={!!errors.ssn}
                  // data-lo-mask: SSN must NEVER appear in session recordings.
                  // This is the most critical masking attribute in this form.
                  data-lo-mask
                />
                <Form.Control.Feedback type="invalid">
                  {errors.ssn}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label>Select your employment authorization status *</Form.Label>
                <Form.Select
                  name="attestation"
                  value={formData.attestation}
                  onChange={handleChange}
                  required
                  isInvalid={!!errors.attestation}
                >
                  <option value="">-- Please select your status --</option>
                  <option value="citizen">A citizen of the United States</option>
                  <option value="noncitizen">A noncitizen national of the United States</option>
                  <option value="lawful_permanent_resident">A lawful permanent resident</option>
                  <option value="alien_authorized_to_work">An alien authorized to work until</option>
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.attestation}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <div className="alert alert-info">
            <small>
              <strong>Note:</strong> You must select one of the above options to certify your eligibility to work in the United States.
            </small>
          </div>
        </Card.Body>
      </Card>

      {/* Emergency Contact Information */}
      <Card className="mb-4 shadow-sm">
        <Card.Header className="bg-info text-white">
          <h5 className="mb-0">🚨 Emergency Contact Information</h5>
        </Card.Header>
        <Card.Body>
          {/* Primary Emergency Contact */}
          <h6 className="mb-3 text-primary">Primary Emergency Contact</h6>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Full Name *</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.emergencyContactName}
                  onChange={(e) => setFormData(prev => ({ ...prev, emergencyContactName: e.target.value }))}
                  placeholder="Enter primary emergency contact full name"
                  isInvalid={!!errors.emergencyContactName}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.emergencyContactName}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Relationship *</Form.Label>
                <Form.Select
                  value={formData.emergencyContactRelationship}
                  onChange={(e) => setFormData(prev => ({ ...prev, emergencyContactRelationship: e.target.value }))}
                  isInvalid={!!errors.emergencyContactRelationship}
                >
                  <option value="">-- Select Relationship --</option>
                  <option value="spouse">Spouse</option>
                  <option value="parent">Parent</option>
                  <option value="child">Child</option>
                  <option value="sibling">Sibling</option>
                  <option value="friend">Friend</option>
                  <option value="other">Other</option>
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.emergencyContactRelationship}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Phone Number *</Form.Label>
                <Form.Control
                  type="tel"
                  value={formData.emergencyContactPhone}
                  onChange={(e) => setFormData(prev => ({ ...prev, emergencyContactPhone: e.target.value }))}
                  placeholder="(555) 123-4567"
                  isInvalid={!!errors.emergencyContactPhone}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.emergencyContactPhone}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Email Address *</Form.Label>
                <Form.Control
                  type="email"
                  value={formData.emergencyContactEmail}
                  onChange={(e) => setFormData(prev => ({ ...prev, emergencyContactEmail: e.target.value }))}
                  placeholder="primary@example.com"
                  isInvalid={!!errors.emergencyContactEmail}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.emergencyContactEmail}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <hr className="my-4" />

          {/* Secondary Emergency Contact */}
          <h6 className="mb-3 text-secondary">Secondary Emergency Contact (Optional)</h6>
          <div className="alert alert-light border-secondary mb-3">
            <small className="text-muted">
              <strong>Note:</strong> If you provide any secondary emergency contact information, all fields in this section become required.
            </small>
          </div>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Full Name</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.secondaryEmergencyContactName}
                  onChange={(e) => setFormData(prev => ({ ...prev, secondaryEmergencyContactName: e.target.value }))}
                  placeholder="Enter secondary emergency contact full name"
                  isInvalid={!!errors.secondaryEmergencyContactName}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.secondaryEmergencyContactName}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Relationship</Form.Label>
                <Form.Select
                  value={formData.secondaryEmergencyContactRelationship}
                  onChange={(e) => setFormData(prev => ({ ...prev, secondaryEmergencyContactRelationship: e.target.value }))}
                  isInvalid={!!errors.secondaryEmergencyContactRelationship}
                >
                  <option value="">-- Select Relationship --</option>
                  <option value="spouse">Spouse</option>
                  <option value="parent">Parent</option>
                  <option value="child">Child</option>
                  <option value="sibling">Sibling</option>
                  <option value="friend">Friend</option>
                  <option value="other">Other</option>
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.secondaryEmergencyContactRelationship}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Phone Number</Form.Label>
                <Form.Control
                  type="tel"
                  value={formData.secondaryEmergencyContactPhone}
                  onChange={(e) => setFormData(prev => ({ ...prev, secondaryEmergencyContactPhone: e.target.value }))}
                  placeholder="(555) 123-4567"
                  isInvalid={!!errors.secondaryEmergencyContactPhone}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.secondaryEmergencyContactPhone}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Email Address</Form.Label>
                <Form.Control
                  type="email"
                  value={formData.secondaryEmergencyContactEmail}
                  onChange={(e) => setFormData(prev => ({ ...prev, secondaryEmergencyContactEmail: e.target.value }))}
                  placeholder="secondary@example.com"
                  isInvalid={!!errors.secondaryEmergencyContactEmail}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.secondaryEmergencyContactEmail}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Navigation */}
      <div className="d-flex justify-content-end">
        <Button 
          onClick={handleNext} 
          size="lg"
          disabled={isValidating}
        >
          {isValidating ? "Validating..." : "Next →"}
        </Button>
      </div>
    </div>
  );


const renderStep2 = () => (
  <div>
    <h4 className="mb-4 text-center">💰 Direct Deposit Setup</h4>
    
    {/* Direct Deposit */}
    <Card className="mb-4">
      <Card.Header className="bg-warning text-dark">
        <div className="d-flex align-items-center justify-content-between">
          <h5 className="mb-0">💳 Direct Deposit</h5>
          
        </div>
      </Card.Header>
      <Card.Body>
        {/* Banking Information Guide */}
        <Card className="mb-4 border-info">
          <Card.Header className="bg-info text-white">
            <h5 className="mb-0">📋 How to Find Your Banking Information</h5>
          </Card.Header>
          <Card.Body>
            <div className="row mb-4">
              <div className="col-lg-6">
                <p className="mb-3 text-muted">
                  Use the sample check below to locate your banking information. Click the image to enlarge it for better visibility.
                </p>
                
                {/* Banking Information Fields Guide */}
                <div className="row g-2">
                  <div className="col-6">
                    <div className="card border-0 bg-primary text-white text-center p-2">
                      <div className="fw-bold">🏦 Bank Name</div>
                      <small>Top of check</small>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="card border-0 bg-success text-white text-center p-2">
                      <div className="fw-bold">💳 Account Type</div>
                      <small>Checking/Savings</small>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="card border-0 bg-warning text-dark text-center p-2">
                      <div className="fw-bold">🔢 Routing #</div>
                      <small>9 digits (first)</small>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="card border-0 bg-secondary text-white text-center p-2">
                      <div className="fw-bold">📝 Account #</div>
                      <small>Middle numbers</small>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="col-lg-6">
                {/* Sample Check Image with Enlarger */}
                <div className="text-center">
                  <div className="position-relative d-inline-block">
                    <img 
                      src="/images/Sample_Check_Image.png" 
                      alt="Sample Check showing routing and account numbers"
                      className="img-fluid border rounded shadow-sm"
                      style={{ 
                        maxWidth: '100%', 
                        height: 'auto', 
                        maxHeight: '250px',
                        cursor: 'pointer',
                        transition: 'transform 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.02)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                      }}
                      onClick={() => {
                        // Create modal for enlarged image
                        const modal = document.createElement('div');
                        modal.id = 'check-image-modal';
                        modal.style.cssText = `
                          position: fixed;
                          top: 0;
                          left: 0;
                          width: 100%;
                          height: 100%;
                          background: rgba(0,0,0,0.8);
                          display: flex;
                          justify-content: center;
                          align-items: center;
                          z-index: 9999;
                          cursor: pointer;
                        `;
                        
                        const img = document.createElement('img');
                        img.src = '/images/Sample_Check_Image.png';
                        img.style.cssText = `
                          max-width: 95%;
                          max-height: 95%;
                          border-radius: 8px;
                          box-shadow: 0 4px 20px rgba(0,0,0,0.5);
                          cursor: default;
                        `;
                        
                        const closeBtn = document.createElement('button');
                        closeBtn.innerHTML = '✕';
                        closeBtn.style.cssText = `
                          position: absolute;
                          top: 20px;
                          right: 30px;
                          background: white;
                          border: none;
                          border-radius: 50%;
                          width: 40px;
                          height: 40px;
                          font-size: 20px;
                          cursor: pointer;
                          box-shadow: 0 2px 10px rgba(0,0,0,0.3);
                          z-index: 10000;
                        `;
                        
                        // Function to safely close modal
                        const closeModal = () => {
                          try {
                            const existingModal = document.getElementById('check-image-modal');
                            if (existingModal && existingModal.parentNode) {
                              existingModal.parentNode.removeChild(existingModal);
                            }
                          } catch (error) {
                            console.log('Modal already removed');
                          }
                        };
                        
                        // Prevent image click from closing modal
                        img.onclick = (e) => {
                          e.stopPropagation();
                        };
                        
                        modal.appendChild(img);
                        modal.appendChild(closeBtn);
                        document.body.appendChild(modal);
                        
                        // Close modal on background click
                        modal.onclick = closeModal;
                        closeBtn.onclick = closeModal;
                        
                        // Close modal on Escape key
                        const handleEscape = (e: KeyboardEvent) => {
                          if (e.key === 'Escape') {
                            closeModal();
                            document.removeEventListener('keydown', handleEscape);
                          }
                        };
                        document.addEventListener('keydown', handleEscape);
                      }}
                    />
                    <div className="position-absolute top-0 end-0 m-1">
                      <span className="badge bg-primary">🔍 Click to enlarge</span>
                    </div>
                  </div>
                  <div className="mt-2">
                    <small className="text-muted fst-italic">
                      Click the sample check above for a detailed view of where to find your banking information
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </Card.Body>
        </Card>

        {/* Net Pay Direct Deposit (Required) - Moved to Top */}
        <Card className="mb-4 border-success">
          <Card.Header className="bg-success text-white">
            <div className="d-flex align-items-center">
              <i className="fas fa-university me-2" aria-hidden="true"></i>
              <h5 className="mb-0">💰 Net Pay Direct Deposit (Required)</h5>
            </div>
          </Card.Header>
          <Card.Body>
            <div className="alert alert-light border-success mb-4">
              <div className="d-flex align-items-center">
                <i className="fas fa-info-circle text-success me-2"></i>
                <p className="mb-0">
                  <strong>Required:</strong> Please provide the account information where your net pay should be deposited.
                  This is where the remaining balance of your paycheck will be sent after any optional withholdings.
                </p>
              </div>
            </div>

            
        
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold text-success d-flex align-items-center">
                <span className="me-2">
                  <i className="fas fa-university" aria-hidden="true"></i>
                  <span className="d-none">🏦</span>
                </span>
                Net Pay Bank Name *
              </Form.Label>
              <Form.Control
                type="text"
                value={formData.secondaryBankName}
                onChange={(e) => setFormData(prev => ({ ...prev, secondaryBankName: e.target.value }))}
                placeholder="Enter net pay bank name"
                isInvalid={!!errors.secondaryBankName}
                required
              />
              <Form.Control.Feedback type="invalid">
                {errors.secondaryBankName}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold text-success d-flex align-items-center">
                <span className="me-2">
                  <i className="fas fa-credit-card" aria-hidden="true"></i>
                  <span className="d-none">💳</span>
                </span>
                Net Pay Account Type *
              </Form.Label>
              <Form.Select
                value={formData.secondaryAccountType}
                onChange={(e) => setFormData(prev => ({ ...prev, secondaryAccountType: e.target.value }))}
                isInvalid={!!errors.secondaryAccountType}
                required
              >
                <option value="checking">Checking</option>
                <option value="savings">Savings</option>
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {errors.secondaryAccountType}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>
        
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold text-success d-flex align-items-center">
                <span className="me-2">
                  <i className="fas fa-hashtag" aria-hidden="true"></i>
                  <span className="d-none">🔢</span>
                </span>
                Net Pay 9-Digit Routing # *
              </Form.Label>
              <Form.Control
                type="text"
                value={formData.secondaryRoutingNumber}
                onChange={(e) => setFormData(prev => ({ ...prev, secondaryRoutingNumber: e.target.value.replace(/\D/g, '').slice(0, 9) }))}
                placeholder="123456789"
                maxLength={9}
                isInvalid={!!errors.secondaryRoutingNumber}
                required
                // data-lo-mask: bank routing numbers are financial PII
                data-lo-mask
              />
              <Form.Control.Feedback type="invalid">
                {errors.secondaryRoutingNumber}
              </Form.Control.Feedback>
              <Form.Text className="text-muted">
                <i className="fas fa-map-marker-alt me-1"></i>
                Found at the bottom left of your check
              </Form.Text>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-4">
              <Form.Label className="fw-bold text-success d-flex align-items-center">
                <span className="me-2">
                  <i className="fas fa-user-circle" aria-hidden="true"></i>
                  <span className="d-none">👤</span>
                </span>
                Net Pay Account # *
              </Form.Label>
              <Form.Control
                type="text"
                value={formData.secondaryAccountNumber}
                onChange={(e) => setFormData(prev => ({ ...prev, secondaryAccountNumber: e.target.value }))}
                placeholder="Your net pay account number"
                isInvalid={!!errors.secondaryAccountNumber}
                required
                // data-lo-mask: bank account numbers are financial PII
                data-lo-mask
              />
              <Form.Control.Feedback type="invalid">
                {errors.secondaryAccountNumber}
              </Form.Control.Feedback>
              <Form.Text className="text-muted">
                <i className="fas fa-map-marker-alt me-1"></i>
                Found in the middle section of your check
              </Form.Text>
            </Form.Group>
          </Col>
        </Row>
          </Card.Body>
        </Card>

        {/* Optional Withholding Information */}
        <Card className="mb-4 border-warning">
          <Card.Header className="d-flex flex-wrap align-items-center gap-3 bg-warning bg-opacity-10 border-warning">
            <div className="d-flex align-items-center gap-3 flex-grow-1">
              <div className="rounded-circle bg-warning text-dark d-flex align-items-center justify-content-center" style={{ width: 42, height: 42 }}>
                <i className="fas fa-piggy-bank" aria-hidden="true"></i>
              </div>
              <div>
                <div className="fw-semibold text-warning">Optional Withholding Account</div>
                <small className="text-muted">
                  Leave collapsed if you want your full paycheck sent to your net pay account.
                </small>
              </div>
            </div>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              {withholdingSummaryChips.map(({ text, variant }) => (
                <Badge key={`withholding-chip-${text}`} bg={variant} className="text-capitalize">
                  {text}
                </Badge>
              ))}
              {hasWithholdingErrors && (
                <Badge bg="danger">Action needed</Badge>
              )}
            </div>
            <Button
              variant={optionalWithholdingOpen ? "warning" : "outline-warning"}
              className="ms-auto"
              onClick={() => {
                optionalWithholdingTouchedRef.current = true;
                setOptionalWithholdingOpen((prev) => !prev);
              }}
              aria-expanded={optionalWithholdingOpen}
              aria-controls="optional-withholding-fields"
            >
              {optionalWithholdingOpen ? "Hide optional fields" : "Add an optional split"}
            </Button>
          </Card.Header>
          <Collapse in={optionalWithholdingOpen}>
            <div id="optional-withholding-fields">
              <Card.Body className="pt-3">
                <Alert variant="light" className="border-warning d-flex align-items-start gap-3">
                  <i className="fas fa-info-circle text-warning mt-1" aria-hidden="true"></i>
                  <div>
                    <strong>Everything inside is optional.</strong> Set a fixed amount to withhold each paycheck—for taxes, savings, or another purpose.
                  </div>
                </Alert>

                <Row className="g-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="text-muted small text-uppercase fw-semibold">
                        <i className="fas fa-bank me-2"></i>
                        Withholding bank name
                      </Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.bankName}
                        onChange={(e) => setFormData(prev => ({ ...prev, bankName: e.target.value }))}
                        placeholder="Bank handling the withheld amount"
                        isInvalid={!!errors.bankName}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.bankName}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="text-muted small text-uppercase fw-semibold">
                        <i className="fas fa-credit-card me-2"></i>
                        Account type
                      </Form.Label>
                      <Form.Select
                        value={formData.accountType}
                        onChange={(e) => setFormData(prev => ({ ...prev, accountType: e.target.value }))}
                        isInvalid={!!errors.accountType}
                      >
                        <option value="">Select account type (optional)</option>
                        <option value="checking">Checking</option>
                        <option value="savings">Savings</option>
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {errors.accountType}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="text-muted small text-uppercase fw-semibold">
                        <i className="fas fa-hashtag me-2"></i>
                        9-digit routing number
                      </Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.routingNumber}
                        onChange={(e) => setFormData(prev => ({ ...prev, routingNumber: e.target.value.replace(/\D/g, '').slice(0, 9) }))}
                        placeholder="123456789"
                        maxLength={9}
                        isInvalid={!!errors.routingNumber}
                        // data-lo-mask: bank routing numbers are financial PII
                        data-lo-mask
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.routingNumber}
                      </Form.Control.Feedback>
                      <Form.Text className="text-muted">
                        Found on the bottom left of your check.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="text-muted small text-uppercase fw-semibold">
                        <i className="fas fa-user-circle me-2"></i>
                        Account number
                      </Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.accountNumber}
                        onChange={(e) => setFormData(prev => ({ ...prev, accountNumber: e.target.value }))}
                        placeholder="Account receiving the withholding"
                        isInvalid={!!errors.accountNumber}
                        // data-lo-mask: bank account numbers are financial PII
                        data-lo-mask
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.accountNumber}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="text-muted small text-uppercase fw-semibold">
                        <i className="fas fa-dollar-sign me-2"></i>
                        Amount to withhold (per paycheck)
                      </Form.Label>
                      <InputGroup hasValidation>
                        <InputGroup.Text>$</InputGroup.Text>
                        <Form.Control
                          type="text"
                          value={formData.depositAmount}
                          onChange={(e) => setFormData(prev => ({ ...prev, depositAmount: e.target.value }))}
                          placeholder="Example: 250.00"
                          isInvalid={!!errors.depositAmount}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.depositAmount}
                        </Form.Control.Feedback>
                      </InputGroup>
                      <Form.Text className="text-muted">
                        Leave blank if you don't want to withhold a fixed amount.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6} className="d-flex align-items-center">
                    <div className="bg-light border rounded-3 w-100 p-3">
                      <div className="fw-semibold mb-1">
                        Need a hand deciding?
                      </div>
                      <small className="text-muted">
                        Most employees keep this blank. Use it if you want an automatic transfer to savings, taxes, or another institution.
                      </small>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </div>
          </Collapse>
        </Card>

        {/* Direct Deposit Authorization Section */}
        <Card className="border-warning mb-4">
          <Card.Header className="bg-warning text-dark">
            <div className="d-flex align-items-center">
              <span className="me-2">📝</span>
              <h5 className="mb-0">Direct Deposit Authorization Agreement</h5>
            </div>
          </Card.Header>
          <Card.Body>
            {/* Authorization Text */}
            <div className="alert alert-light border-warning mb-4">
              <div className="row align-items-center">
                <div className="col-lg-8">
                  <h6 className="text-warning mb-2">
                    <i className="fas fa-university me-2"></i>
                    Authorization Statement
                  </h6>
                  <p className="mb-2 lh-base">
                    I, <strong>{formData.firstName} {formData.lastName}</strong>, hereby authorize 
                    <strong> Site Development, Inc.</strong> to directly deposit my payroll payments to the bank account(s) 
                    specified above.
                  </p>
                  <p className="mb-0 small text-muted">
                    <i className="fas fa-info-circle me-1"></i>
                    This authorization remains in effect until written notice is provided to modify or cancel it.
                  </p>
                </div>
                <div className="col-lg-4 text-lg-end mt-3 mt-lg-0">
                  <div className="bg-warning bg-opacity-10 p-3 rounded border border-warning">
                    <div className="text-warning fw-bold mb-1">
                      <i className="fas fa-shield-alt me-1"></i>
                      Secure Process
                    </div>
                    <small className="text-muted">
                      Your banking information is encrypted and securely processed
                    </small>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Signature and Date Section */}
            <div className="row">
              <div className="col-lg-8">
                <div className="card border-secondary h-100">
                  <div className="card-header bg-secondary text-white">
                    <h6 className="mb-0">
                      <i className="fas fa-signature me-2"></i>
                      Employee Signature Required
                    </h6>
                  </div>
                  <div className="card-body">
                    {formData.signature ? (
                      <div>
                        <div className="d-flex align-items-center text-success mb-3">
                          <i className="fas fa-check-circle me-2"></i>
                          <span className="fw-bold">Digital signature captured successfully</span>
                        </div>
                        <div className="text-center p-3 bg-light rounded border">
                          <img 
                            src={formData.signature} 
                            alt="Digital Signature" 
                            style={{ 
                              maxHeight: '80px', 
                              maxWidth: '100%',
                              border: '1px solid #ddd', 
                              padding: '10px',
                              borderRadius: '4px',
                              backgroundColor: 'white'
                            }}
                          />
                        </div>
                        <div className="text-center mt-2">
                          <small className="text-muted">
                            <i className="fas fa-user me-1"></i>
                            Signature of {formData.firstName} {formData.lastName}
                          </small>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center p-4">
                        <div className="text-muted mb-3">
                          <i className="fas fa-pen-nib fa-2x mb-2 d-block"></i>
                          <span>Digital signature will be captured in the final step</span>
                        </div>
                        <div className="alert alert-info mb-0">
                          <small>
                            <i className="fas fa-info-circle me-1"></i>
                            You'll be able to sign digitally using your mouse, touchpad, or touch screen
                          </small>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="col-lg-4 mt-3 mt-lg-0">
                <div className="card border-info h-100">
                  <div className="card-header bg-info text-white">
                    <h6 className="mb-0">
                      <i className="fas fa-calendar-alt me-2"></i>
                      Authorization Date
                    </h6>
                  </div>
                  <div className="card-body">
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-bold text-info">Date Signed</Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.directDepositDate}
                        readOnly
                        className="bg-light border-info"
                        style={{ fontSize: '1.1rem', fontWeight: '500' }}
                      />
                      <Form.Text className="text-muted">
                        <i className="fas fa-lock me-1"></i>
                        Automatically set to current date
                      </Form.Text>
                    </Form.Group>
                    
                    <div className="alert alert-light border-info mb-0">
                      <small className="text-info">
                        <i className="fas fa-clock me-1"></i>
                        <strong>Processing Time:</strong> Direct deposit typically takes 1-2 pay cycles to activate
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Card.Body>
        </Card>
      </Card.Body>
    </Card>

    {/* Navigation Buttons */}
    <div className="d-flex justify-content-between">
      <Button 
        variant="secondary" 
        onClick={handlePrevious} 
        size="lg"
        disabled={isValidating}
      >
        ← Previous
      </Button>
      <Button 
        variant="primary" 
        onClick={handleNext} 
        size="lg"
        disabled={isValidating}
      >
        {isValidating ? "Validating..." : "Next: Documents & Signature →"}
      </Button>
    </div>
  </div>
);

const renderStep3 = () => (
  <div>
    <h3 className="mb-4 text-center">Final Step: Documents & Verification</h3>

    {/* Candidate Photo Section */}
    <Card className="mb-4 shadow-sm">
      <Card.Header className="bg-primary text-white">
        <h5 className="mb-0">📸 Candidate Photo</h5>
      </Card.Header>
      <Card.Body>
        <p className="text-muted mb-3">Please provide your photo for identification purposes</p>
        
        {/* Photo Choice Tabs */}
        <div className="mb-3">
          <div className="d-flex gap-2">
            <Button
              variant={formData.imageChoice === "upload" ? "primary" : "outline-primary"}
              onClick={() => setFormData({ ...formData, imageChoice: "upload", userPhotoPreview: null })}
            >
              📁 Upload Photo
            </Button>
            <Button
              variant={formData.imageChoice === "camera" ? "primary" : "outline-primary"}
              onClick={() => setFormData({ ...formData, imageChoice: "camera", userPhotoPreview: null })}
            >
              📷 Take Photo
            </Button>
          </div>
          {errors.userPhoto && (
            <div className="text-danger small mt-1">{errors.userPhoto}</div>
          )}
        </div>

        {/* Upload Tab Content */}
        {formData.imageChoice === "upload" && (
          <Form.Group className="mb-3">
            <Form.Label>Select Image File</Form.Label>
            <Form.Control type="file" accept="image/*" onChange={handleUserPhotoChange} />
            {formData.userPhotoPreview && (
              <div className="mt-3 text-center">
                <img
                  src={formData.userPhotoPreview}
                  alt="User Photo Preview"
                  style={{ 
                    maxWidth: "200px", 
                    maxHeight: "200px", 
                    borderRadius: "8px",
                    border: "2px solid #dee2e6"
                  }}
                />
                <p className="text-success mt-2">✓ Photo uploaded successfully</p>
              </div>
            )}
          </Form.Group>
        )}

        {/* Camera Tab Content */}
        {formData.imageChoice === "camera" && (
          <div>
            <Form.Label>Capture Photo</Form.Label>
            <ImageCapture
              onImageSelect={(previewUrl: string, blob: Blob) => {
                setFormData((prev) => ({
                  ...prev,
                  userPhoto: blob instanceof File ? blob : new File([blob], "camera-photo.png"),
                  userPhotoPreview: previewUrl,
                }));
              }}
            />
          </div>
        )}
      </Card.Body>
    </Card>

    {/* Supporting Documents Section */}
    <Card className="mb-4 shadow-sm">
      <Card.Header className="bg-success text-white">
        <h5 className="mb-0">📄 Supporting Documents</h5>
      </Card.Header>
      <Card.Body>
        <p className="text-muted mb-3">Please select and upload your identification documents</p>
        
        {/* Document Type Selection - Badge Style */}
        <Form.Group className="mb-4">
          <Form.Label className="fw-bold">Document Types *</Form.Label>
          <p className="text-muted small mb-2">Click to select document types (you can select multiple)</p>
          <div className="d-flex flex-wrap gap-2">
            {["SSN Card", "Driver's License", "Passport", "State ID", "Birth Certificate", "Utility Bill"].map((docType) => (
              <Button
                key={docType}
                variant={formData.documentType.includes(docType) ? "primary" : "outline-secondary"}
                size="sm"
                className={`rounded-pill px-3 py-2 ${formData.documentType.includes(docType) ? 'shadow-sm' : ''}`}
                onClick={() => handleDocumentTypeToggle(docType)}
                style={{
                  transition: "all 0.2s ease",
                  fontWeight: formData.documentType.includes(docType) ? "600" : "normal"
                }}
              >
                {formData.documentType.includes(docType) && "✓ "}
                {docType}
              </Button>
            ))}
          </div>
          {formData.documentType.length > 0 && (
            <div className="mt-2">
              <small className="text-success">
                ✓ Selected: {formData.documentType.join(", ")}
              </small>
            </div>
          )}
          {errors.documentType && (
            <div className="text-danger small mt-1">{errors.documentType}</div>
          )}
        </Form.Group>

        {/* Document Upload - Multiple Files */}
        <Form.Group className="mb-3">
          <Form.Label className="fw-bold">Upload Documents *</Form.Label>
          <Form.Control 
            type="file" 
            accept="image/*,.pdf" 
            onChange={handleFileChange}
            className="mb-2"
            multiple
          />
          <Form.Text className="text-muted">
            Accepted formats: PDF, JPG, PNG (Max size: 10MB each) • You can select multiple files
          </Form.Text>
          {errors.documentFiles && (
            <div className="text-danger small mt-1">{errors.documentFiles}</div>
          )}
        </Form.Group>

        {/* Uploaded Documents List */}
        {formData.documentFiles.length > 0 && (
          <div className="mt-3">
            <Form.Label className="fw-bold">Uploaded Documents ({formData.documentFiles.length})</Form.Label>
            <div className="border rounded p-3 bg-light">
              {formData.documentFiles.map((file, index) => (
                <div key={index} className="d-flex justify-content-between align-items-center mb-2 p-2 bg-white rounded border">
                  <div className="d-flex align-items-center">
                    <span className="me-2">
                      {file.type.includes('pdf') ? '📄' : '🖼️'}
                    </span>
                    <div>
                      <div className="fw-medium">{file.name}</div>
                      <small className="text-muted">
                        {(file.size / 1024 / 1024).toFixed(2)} MB • {file.type}
                      </small>
                    </div>
                  </div>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => handleRemoveDocument(index)}
                    className="ms-2"
                  >
                    🗑️ Remove
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
      </Card.Body>
    </Card>

    {/* Signature Section */}
    <Card className="mb-4 shadow-sm">
      <Card.Header className="bg-warning text-dark">
        <h5 className="mb-0">✍️ Digital Signature</h5>
      </Card.Header>
      <Card.Body>
        <Row>
          <Col md={8}>
            <p className="text-muted mb-3">
              By signing below, you certify that all information provided is true and accurate
            </p>
            
            {/* Signature Type Selection */}
            <div className="mb-3">
              <Form.Label className="fw-bold mb-2">Signature Method *</Form.Label>
              <div className="d-flex gap-2 mb-3">
                <Button
                  variant={formData.signatureType === "type" ? "primary" : "outline-primary"}
                  size="sm"
                  onClick={() => setFormData(prev => ({ ...prev, signatureType: "type", signature: "" }))}
                  className="d-flex align-items-center gap-2"
                >
                  ⌨️ Type Name
                </Button>
                <Button
                  variant={formData.signatureType === "draw" ? "primary" : "outline-primary"}
                  size="sm"
                  onClick={() => setFormData(prev => ({ ...prev, signatureType: "draw", signature: "" }))}
                  className="d-flex align-items-center gap-2"
                >
                  ✏️ Draw
                </Button>
                <Button
                  variant={formData.signatureType === "upload" ? "primary" : "outline-primary"}
                  size="sm"
                  onClick={() => setFormData(prev => ({ ...prev, signatureType: "upload", signature: "" }))}
                  className="d-flex align-items-center gap-2"
                >
                  📄 Upload
                </Button>
              </div>
              
              <div className="text-muted small mb-3">
                {formData.signatureType === "type" && "💡 Typed signatures are more legible and professional"}
                {formData.signatureType === "draw" && "💡 Draw your signature using mouse or touch"}
                {formData.signatureType === "upload" && "💡 Upload a clear image of your signature"}
              </div>
            </div>
            
            {/* Typed Signature Input */}
            {formData.signatureType === "type" && (
              <div className="mb-3">
                <Form.Label className="fw-bold mb-2">Type Your Full Name *</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.typedSignature}
                  onChange={(e) => setFormData(prev => ({ ...prev, typedSignature: e.target.value }))}
                  placeholder="Enter your full legal name"
                  className="mb-2"
                />
                {formData.typedSignature && (
                  <div className="border rounded p-3 bg-light mb-2">
                    <div className="text-muted small mb-1">Preview:</div>
                    <div style={{
                      fontFamily: 'Brush Script MT, cursive',
                      fontSize: '24px',
                      color: '#1a365d',
                      fontWeight: 'bold'
                    }}>
                      {formData.typedSignature}
                    </div>
                  </div>
                )}
                <Button
                  variant="primary"
                  size="sm"
                  onClick={saveSignature}
                  disabled={!formData.typedSignature.trim()}
                  className="d-flex align-items-center gap-1"
                >
                  💾 Save Typed Signature
                </Button>
              </div>
            )}
            
            {/* Drawing Signature Canvas */}
            {formData.signatureType === "draw" && (
              <div className="mb-3">
                <Form.Label className="fw-bold mb-2">Draw Your Signature *</Form.Label>
                <div className="border rounded p-2 bg-white" style={{ width: "100%", maxWidth: "450px" }}>
                  <SignatureCanvas
                    ref={sigCanvas}
                    penColor="black"
                    canvasProps={{
                      width: 420,
                      height: 150,
                      className: "w-100 h-100",
                      style: { background: "white" }
                    }}
                  />
                </div>
                
                {/* Drawing Signature Control Buttons */}
                <div className="mt-2 d-flex gap-2">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={clearSignature}
                    className="d-flex align-items-center gap-1"
                  >
                    🗑️ Clear
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={saveSignature}
                    className="d-flex align-items-center gap-1"
                  >
                    💾 Save
                  </Button>
                </div>
              </div>
            )}
            
            {/* Upload Signature */}
            {formData.signatureType === "upload" && (
              <div className="mb-3">
                <Form.Label className="fw-bold mb-2">Upload Signature Image *</Form.Label>
                <Form.Control
                  type="file"
                  accept="image/*"
                  onChange={handleSignatureUpload}
                  className="mb-2"
                />
                <div className="text-muted small">
                  Please upload a clear image of your signature (PNG, JPG, or JPEG)
                </div>
              </div>
            )}
            
            {/* Signature Success/Error Messages */}
            {formData.signature && (
              <div className="alert alert-success d-flex align-items-center py-2">
                <span className="me-2">✓</span>
                <span>Signature saved successfully</span>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={clearSignature}
                  className="ms-auto"
                >
                  Clear
                </Button>
              </div>
            )}
            
            {errors.signature && (
              <div className="alert alert-danger py-2">{errors.signature}</div>
            )}
          </Col>
          
          <Col md={4}>
            <div className="alert alert-info h-100 d-flex align-items-center">
              <div>
                <div className="fw-bold mb-2">Legal Notice</div>
                <small>
                  Your digital signature has the same legal validity as a handwritten signature and confirms your agreement to all terms stated in this form.
                </small>
              </div>
            </div>
          </Col>
        </Row>
        
        <div className="mt-3 p-3 bg-light rounded">
          <div className="d-flex align-items-center">
            <span className="me-2">📋</span>
            <small className="text-muted">
              <strong>Certification:</strong> I attest, under penalty of perjury, that I am (a) a U.S. citizen or national, (b) a lawful permanent resident, or (c) an alien authorized to work in the United States.
            </small>
          </div>
        </div>
      </Card.Body>
    </Card>

    {/* Disclaimer Acknowledgement */}
    <Card className="mb-4">
      <Card.Header className="bg-warning text-dark">
        <h5 className="mb-0">⚠️ Important Disclaimer & Acknowledgement</h5>
      </Card.Header>
      <Card.Body>
        <div className="alert alert-warning border-warning">
          <h6 className="fw-bold mb-3">EMPLOYMENT VERIFICATION DISCLAIMER</h6>
          <div className="mb-3">
            <p className="mb-2">
              <strong>By submitting this form, I acknowledge and agree to the following:</strong>
            </p>
            <ul className="mb-3">
              <li>All information provided in this form is true, complete, and accurate to the best of my knowledge</li>
              <li>I understand that any false statements or omissions may result in termination of employment</li>
              <li>I consent to verification of the information provided through government databases and other sources</li>
              <li>I understand that employment eligibility verification is required by federal law</li>
              <li>I agree to provide acceptable documentation to verify my identity and work authorization</li>
              <li>I understand that this information will be kept confidential and used only for employment verification purposes</li>
            </ul>
          </div>
          
          {/* <div className="bg-light p-3 rounded border">
            <p className="mb-2 fw-bold text-danger">
              ⚖️ LEGAL NOTICE: Federal law requires all employers to verify the identity and employment eligibility of all employees.
            </p>
            <small className="text-muted">
              This form complies with Section 274A of the Immigration and Nationality Act and related federal regulations.
            </small>
          </div> */}
        </div>

        <Form.Check
          type="checkbox"
          id="disclaimerAcknowledged"
          checked={formData.disclaimerAcknowledged}
          onChange={(e) => setFormData(prev => ({ ...prev, disclaimerAcknowledged: e.target.checked }))}
          className="mb-3"
          label={
            <span className="fw-bold">
              ✓ I have read, understood, and agree to all terms stated in this disclaimer
            </span>
          }
        />

        {errors.disclaimerAcknowledged && (
          <div className="alert alert-danger py-2 mt-2">
            <small>{errors.disclaimerAcknowledged}</small>
          </div>
        )}

        <div className="text-muted small mt-2">
          <span className="text-danger">*</span> This acknowledgement is required before form submission
        </div>
      </Card.Body>
    </Card>

    {/* Navigation Buttons */}
    <div className="d-flex justify-content-between">
      <Button 
        variant="secondary" 
        onClick={handlePrevious} 
        size="lg"
        disabled={isSubmitting}
      >
        ← Previous
      </Button>
      <Button 
        type="submit" 
        variant="success" 
        size="lg"
        disabled={isValidating || isSubmitting || submitSuccess || !formData.disclaimerAcknowledged}
      >
        {isSubmitting ? (
          <>
            <Spinner animation="border" size="sm" className="me-2" />
            Submitting...
          </>
        ) : isValidating ? (
          "Validating..."
        ) : submitSuccess ? (
          "✓ Submitted Successfully"
        ) : (
          "Submit Form →"
        )}
      </Button>
    </div>
  </div>
);


  return (
    <Container className="py-4">
      <h2 className="mb-3 text-center">Employee Packet</h2>
      
      {/* Show user type indicator */}
      {/* <Alert variant={isCandidate ? "primary" : "info"} className="mb-3">
        <div className="d-flex align-items-center">
          <span className="me-2">{isCandidate ? "👤" : "🔐"}</span>
          <strong>
            {isCandidate ? "Candidate Access" : "Internal User Access"}
          </strong>
          {isCandidate && token && (
            <small className="ms-2 text-muted">
              (Token: {token.substring(0, 8)}...)
            </small>
          )}
        </div>
      </Alert> */}
      
      {/* Show application info if loaded from applicationId */}
      {applicationData && (
        <Alert variant="info" className="mb-3">
          <strong>Pre-filling form for:</strong> {applicationData.firstName} {applicationData.lastName} - {applicationData.jobAppliedFor}
        </Alert>
      )}
      
      {/* Success message */}
      {submitSuccess && (
        <Alert variant="success" className="mb-3">
          <Alert.Heading>🎉 Form Submitted Successfully!</Alert.Heading>
          <p>Your Employee Packet has been processed and the following files have been generated:</p>
          <ul>
            <li>Completed Employee Packet PDF</li>
            <li>ZIP archive containing all your documents</li>
          </ul>
          <p className="mb-0">HR will review your submission and contact you if any additional information is needed.</p>
        </Alert>
      )}
      
      {/* Loading state */}
      {isLoadingApplication && (
        <div className="text-center mb-4">
          <Spinner animation="border" role="status" className="me-2" />
          <span>Loading application data...</span>
        </div>
      )}
      
      {/* Error state */}
      {applicationLoadError && (
        <Alert variant="danger" className="mb-3">
          {applicationLoadError}
        </Alert>
      )}
      
      <ProgressBar now={getProgress()} className="mb-4" />

      <Form onSubmit={handleSubmit}>
        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && renderStep2()}
        {currentStep === 3 && renderStep3()}
      </Form>
    </Container>
  );
};

export default EmployeePacket;
